import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../utils/api';
import { buildQuoteLines, calcCFT, calcChargeableWeight, calcVolWeight } from '../utils/quoteCalc';

const ORIGINS = ['USA, Miami', 'Canada', 'Panama'];
const SERVICES = [
  { value: 'ocean', label: 'Ocean LCL' },
  { value: 'air',   label: 'Air Freight' },
  { value: 'fcl20', label: "FCL 20' DV" },
  { value: 'fcl40', label: "FCL 40' HC" },
  { value: 'fcl45', label: "FCL 45' HC" },
];

const TABS = ['Shipment', 'Cargo', 'Options', 'Transport', 'Preview'];

export default function NewQuote() {
  const { clients, rates, settings, toast, setPage } = useApp();
  const [tab, setTab] = useState(0);
  const [brokerageRates, setBrokerageRates] = useState([]);
  const [cifTiers, setCifTiers] = useState([]);
  const [transportZones, setTransportZones] = useState([]);
  const [transportParams, setTransportParams] = useState({});

  const [fields, setFields] = useState({
    clientId: '', clientName: '', clientEmail: '',
    service: 'ocean', origin: 'USA, Miami',
    commodity: '',
    length: '', width: '', height: '', qty: '',
    actualWeightLbs: '',
    commercialValue: '',
    roe: '',
    validity: '',
    notes: '',
    // Options
    caricom: false, sedEei: false, insurance: false,
    inlandPickup: '', freightCollectFee: false,
    includeBrokerage: false, includeTransport: false,
    // Transport
    distanceKm: '', requiresSecondMan: false,
    afterHours: false, tailLift: false, waitingMinutes: '',
    transportZoneId: '',
  });

  const [result, setResult] = useState(null);
  const [transportResult, setTransportResult] = useState(null);
  const [sendModal, setSendModal] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendBody, setSendBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  useEffect(() => {
    api.get('/brokerage-rates').then(setBrokerageRates).catch(() => {});
    api.get('/brokerage-tiers').then(setCifTiers).catch(() => {});
    api.get('/transport-zones').then(setTransportZones).catch(() => {});
    api.get('/transport-params').then(p => {
      const map = {};
      p.forEach(x => map[x.key] = x.value);
      setTransportParams(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (settings?.default_roe && !fields.roe) set('roe', settings.default_roe);
    if (settings?.default_validity && !fields.validity) set('validity', settings.default_validity);
  }, [settings]);

  // Required fields check — preview only shows when all key fields are filled
  const requiredFilled = fields.clientName?.trim() || fields.clientId;
  const cargoDone = fields.length && fields.width && fields.height && fields.qty && fields.actualWeightLbs;
  const shipmentDone = fields.commercialValue && fields.roe && fields.validity;
  const canPreview = requiredFilled && cargoDone && shipmentDone;

  useEffect(() => {
    if (!rates.length || !canPreview) {
      setResult(null);
      return;
    }
    const r = buildQuoteLines(fields, rates, settings, brokerageRates, cifTiers);
    setResult(r);
    // Transport calc
    if (fields.includeTransport && transportZones.length) {
      calcTransportQuote();
    }
  }, [fields, rates, settings, brokerageRates, cifTiers, transportZones, canPreview]);

  const calcTransportQuote = () => {
    const zone = transportZones.find(z => z.zone === parseInt(fields.transportZoneId));
    if (!zone) return;
    const lines = [];
    let total = 0;

    if (zone.base_rate_ttd > 0) {
      lines.push({ description: `Delivery — Zone ${zone.zone} (${zone.example_areas})`, ttd: zone.base_rate_ttd });
      total += zone.base_rate_ttd;
    }

    const extraKm = Math.max(0, (parseFloat(fields.distanceKm)||0) - (zone.distance_max||0));
    if (extraKm > 0 && zone.per_km_ttd > 0) {
      const charge = extraKm * zone.per_km_ttd;
      lines.push({ description: `Extra km (${extraKm.toFixed(1)} km × TT$${zone.per_km_ttd})`, ttd: charge });
      total += charge;
    }

    if (fields.requiresSecondMan && transportParams.second_man_ttd > 0) {
      lines.push({ description: 'Second handler', ttd: transportParams.second_man_ttd });
      total += transportParams.second_man_ttd;
    }
    if (fields.tailLift && transportParams.tail_lift_ttd > 0) {
      lines.push({ description: 'Tail-lift required', ttd: transportParams.tail_lift_ttd });
      total += transportParams.tail_lift_ttd;
    }
    if (fields.afterHours && transportParams.after_hours_ttd > 0) {
      lines.push({ description: 'After-hours delivery', ttd: transportParams.after_hours_ttd });
      total += transportParams.after_hours_ttd;
    }
    const freeMin = transportParams.free_waiting_minutes || 30;
    const billableMin = Math.max(0, (parseInt(fields.waitingMinutes)||0) - freeMin);
    if (billableMin > 0 && transportParams.waiting_block_minutes > 0) {
      const blocks = Math.ceil(billableMin / transportParams.waiting_block_minutes);
      const charge = blocks * (transportParams.waiting_rate_per_block_ttd || 50);
      lines.push({ description: `Waiting time (${billableMin} min, ${blocks} blocks)`, ttd: charge });
      total += charge;
    }
    setTransportResult({ lines, total, zone });
  };

  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));

  const onClientChange = e => {
    const id = e.target.value;
    const client = clients.find(c => String(c.id) === String(id));
    setFields(f => ({ ...f, clientId: id, clientName: client?.name||'', clientEmail: client?.email||'' }));
    if (client) setSendTo(client.email);
  };

  const saveQuote = async (status = 'draft') => {
    // Validate — need at least a client name for manual quotes
    const clientName = fields.clientName?.trim() || (fields.clientId ? clients.find(c=>String(c.id)===String(fields.clientId))?.name : '') || '';
    if (!clientName) {
      toast('Please enter a client name or select a client', 'error');
      setTab(0); // jump back to shipment tab
      return null;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: fields.clientId||null,
        client_name: clientName,
        client_email: fields.clientEmail,
        service: SERVICES.find(s=>s.value===fields.service)?.label||fields.service,
        origin: fields.origin,
        commodity: fields.commodity,
        length_in: fields.length, width_in: fields.width, height_in: fields.height,
        qty: fields.qty, actual_weight_lbs: fields.actualWeightLbs,
        cft: result?.cft, chargeable_weight_lbs: result?.chargeableWeight,
        commercial_value_usd: fields.commercialValue,
        cif_value: result?.cifValueTTD,
        rate_of_exchange: fields.roe,
        validity: fields.validity,
        lines: [
          ...(result?.lines||[]),
          ...(fields.includeBrokerage && result?.brokerageLines?.length ? [
            { description: '── Brokerage ──', usd: null, ttd: null, header: true },
            ...result.brokerageLines,
          ] : []),
          ...(fields.includeTransport && transportResult?.lines?.length ? [
            { description: '── Delivery ──', usd: null, ttd: null, header: true },
            ...transportResult.lines,
          ] : []),
        ],
        total_usd: result?.totalUSD,
        total_ttd: (result?.totalTTD||0) + (fields.includeBrokerage ? result?.brokerageTotal||0 : 0) + (fields.includeTransport ? transportResult?.total||0 : 0),
        includes_brokerage: fields.includeBrokerage,
        includes_transport: fields.includeTransport,
        transport_zone: fields.transportZoneId,
        transport_total_ttd: transportResult?.total||0,
        brokerage_total_ttd: result?.brokerageTotal||0,
        status, notes: fields.notes,
      };
      const resp = await api.post('/quotes', payload);
      setSavedId(resp.id);
      toast('Quote saved as ' + resp.quote_number, 'success');
      return resp;
    } catch(e) { toast(e.message, 'error'); return null; }
    finally { setSaving(false); }
  };

  const openSend = async () => {
    let id = savedId;
    if (!id) { const r = await saveQuote('draft'); if (!r) return; id = r.id; }
    setSendSubject(`Freight Quotation — PAS Trinidad Limited`);
    setSendBody(`Dear ${fields.clientName||'Client'},\n\nPlease find attached your freight quotation.\n\nWe look forward to your confirmation.\n\nBest regards,\nPAS Trinidad Limited`);
    setSendModal(true);
  };

  const doSend = async () => {
    if (!savedId) return;
    setSaving(true);
    try {
      const resp = await api.post(`/quotes/${savedId}/send`, { to: sendTo, subject: sendSubject, body: sendBody });
      setSendModal(false);
      toast(resp.simulated ? 'Simulated — configure SMTP to send real emails' : 'Sent to ' + sendTo, 'success');
    } catch(e) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const fmt = n => n==null||isNaN(n)?'—':Number(n).toFixed(2);
  const fmtTTD = n => n==null?'—':'TT$'+Number(n).toFixed(2);
  const grandTotal = canPreview ? ((result?.totalTTD||0) + (fields.includeBrokerage?result?.brokerageTotal||0:0) + (fields.includeTransport?transportResult?.total||0:0)) : 0;

  const isFCL = ['fcl20','fcl40','fcl45'].includes(fields.service);
  const isAir = fields.service === 'air';

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">New quotation</div><div className="page-sub">Build and send a professional freight quote</div></div>
      </div>

      {/* Tab bar with progress */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:24, borderBottom:'1px solid var(--border)' }}>
        <div style={{ display:'flex', gap:4 }}>
          {TABS.map((t,i) => {
            const tabDone = (i===0 && !!(fields.clientName?.trim()||fields.clientId) && fields.commercialValue && fields.roe && fields.validity)
              || (i===1 && fields.length && fields.width && fields.height && fields.qty && fields.actualWeightLbs)
              || (i===2) || (i===3) || (i===4);
            return (
              <button key={t} onClick={()=>setTab(i)} style={{ padding:'8px 16px', border:'none', background:'none', fontFamily:'inherit', fontSize:13, cursor:'pointer', borderBottom: tab===i ? '2px solid var(--navy)' : '2px solid transparent', color: tab===i ? 'var(--navy)' : 'var(--light)', fontWeight: tab===i ? 600 : 400, marginBottom:-1, display:'flex', alignItems:'center', gap:6 }}>
                {i < 2 && tabDone && <span style={{ color:'#1D9E75', fontSize:11 }}>✓</span>}
                {t}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize:11, color:'var(--light)', marginBottom:8, paddingRight:4 }}>
          <span style={{ color:'#C0392B' }}>*</span> Required fields
        </div>
      </div>

      {/* TAB 0 — Shipment */}
      {tab===0 && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title">Client</div>
            <div className="field">
              <label className="label">Select saved client</label>
              <select className="select" value={fields.clientId} onChange={onClientChange}>
                <option value="">— No client / manual entry —</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {!fields.clientId && <>
              <div className="field"><label className="label">Client name</label><input className="input" value={fields.clientName} onChange={e=>set('clientName',e.target.value)} placeholder="Company name" /></div>
              <div className="field"><label className="label">Client email</label><input className="input" type="email" value={fields.clientEmail} onChange={e=>{set('clientEmail',e.target.value);setSendTo(e.target.value);}} placeholder="email@client.com" /></div>
            </>}
          </div>
          <div className="card">
            <div className="card-title">Service & route</div>
            <div className="field-row">
              <div className="field">
                <label className="label">Service type</label>
                <select className="select" value={fields.service} onChange={e=>set('service',e.target.value)}>
                  {SERVICES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label className="label">Origin</label>
                <select className="select" value={fields.origin} onChange={e=>set('origin',e.target.value)}>
                  {ORIGINS.map(o=><option key={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <label className="label">Rate of exchange (TTD) <span style={{color:'#C0392B'}}>*</span></label>
                <input className="input" type="number" step="0.01" value={fields.roe} onChange={e=>set('roe',e.target.value)} placeholder="e.g. 6.77" />
              </div>
              <div className="field">
                <label className="label">Validity</label>
                <select className="select" value={fields.validity} onChange={e=>set('validity',e.target.value)}>
                  <option>7 days</option><option>14 days</option><option>30 days</option><option>60 days</option>
                </select>
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <label className="label">Commercial value (USD) <span style={{color:'#C0392B'}}>*</span></label>
                <input className="input" type="number" value={fields.commercialValue} onChange={e=>set('commercialValue',e.target.value)} placeholder="Enter USD value" />
              </div>
              <div className="field">
                <label className="label">Commodity</label>
                <input className="input" value={fields.commodity} onChange={e=>set('commodity',e.target.value)} placeholder="e.g. General cargo" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 1 — Cargo */}
      {tab===1 && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title">Dimensions (inches)</div>
            <div className="field-row-3">
              <div className="field"><label className="label">Length (in) <span style={{color:'#C0392B'}}>*</span></label><input className="input" type="number" value={fields.length} onChange={e=>set('length',e.target.value)} placeholder="0" /></div>
              <div className="field"><label className="label">Width (in) <span style={{color:'#C0392B'}}>*</span></label><input className="input" type="number" value={fields.width} onChange={e=>set('width',e.target.value)} placeholder="0" /></div>
              <div className="field"><label className="label">Height (in) <span style={{color:'#C0392B'}}>*</span></label><input className="input" type="number" value={fields.height} onChange={e=>set('height',e.target.value)} placeholder="0" /></div>
            </div>
            <div className="field-row">
              <div className="field"><label className="label">Qty (pieces) <span style={{color:'#C0392B'}}>*</span></label><input className="input" type="number" value={fields.qty} onChange={e=>set('qty',e.target.value)} placeholder="1" /></div>
              <div className="field"><label className="label">Actual weight (lbs) <span style={{color:'#C0392B'}}>*</span></label><input className="input" type="number" value={fields.actualWeightLbs} onChange={e=>set('actualWeightLbs',e.target.value)} placeholder="0" /></div>
            </div>
          </div>
          {result && (
            <div className="card">
              <div className="card-title">Weight & volume summary</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                {[
                  ['CFT (cubic feet)', fmt(result.cft)],
                  ['CBM (cubic metres)', fmt(result.cft * 0.028317)],
                  ['Volumetric weight', fmt(result.volWeight) + ' lbs'],
                  ['Actual weight', fmt(fields.actualWeightLbs) + ' lbs'],
                ].map(([k,v])=>(
                  <div key={k} style={{ background:'var(--cream)', borderRadius:8, padding:'12px 14px' }}>
                    <div style={{ fontSize:11, color:'var(--light)', textTransform:'uppercase', letterSpacing:'0.05em' }}>{k}</div>
                    <div style={{ fontSize:18, fontWeight:600, marginTop:4 }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:16, background:'#EEF3FB', borderRadius:8, padding:'14px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:11, color:'var(--light)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Chargeable weight</div>
                  <div style={{ fontSize:22, fontWeight:700, color:'var(--navy)', marginTop:2 }}>{fmt(result.chargeableWeight)} lbs</div>
                  <div style={{ fontSize:11, color:'var(--light)', marginTop:2 }}>max(actual, volumetric)</div>
                </div>
                {result.chargeableWeight > (parseFloat(fields.actualWeightLbs)||0) && (
                  <span className="badge badge-blue">Vol. weight applies</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 2 — Options */}
      {tab===2 && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title">Optional charges</div>
            {!isFCL && <>
              <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
                <input type="checkbox" checked={fields.caricom} onChange={e=>set('caricom',e.target.checked)} style={{ width:16,height:16 }} />
                <div><div style={{ fontWeight:500, fontSize:14 }}>Caricom required</div><div style={{ fontSize:12, color:'var(--light)' }}>Add Caricom documentation fee</div></div>
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
                <input type="checkbox" checked={fields.sedEei} onChange={e=>set('sedEei',e.target.checked)} style={{ width:16,height:16 }} />
                <div><div style={{ fontWeight:500, fontSize:14 }}>SED / EEI required</div><div style={{ fontSize:12, color:'var(--light)' }}>Shipper's Export Declaration</div></div>
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
                <input type="checkbox" checked={fields.insurance} onChange={e=>set('insurance',e.target.checked)} style={{ width:16,height:16 }} />
                <div><div style={{ fontWeight:500, fontSize:14 }}>Insurance required</div><div style={{ fontSize:12, color:'var(--light)' }}>Cargo insurance</div></div>
              </label>
              {isAir && (
                <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
                  <input type="checkbox" checked={fields.freightCollectFee} onChange={e=>set('freightCollectFee',e.target.checked)} style={{ width:16,height:16 }} />
                  <div><div style={{ fontWeight:500, fontSize:14 }}>Freight Collect Fee (5%)</div><div style={{ fontSize:12, color:'var(--light)' }}>5% of local destination charges</div></div>
                </label>
              )}
              <div className="field" style={{ marginTop:12 }}>
                <label className="label">Inland pick-up (USD)</label>
                <input className="input" type="number" step="0.01" value={fields.inlandPickup} onChange={e=>set('inlandPickup',e.target.value)} placeholder="Leave blank if not applicable" />
              </div>
            </>}
            {isFCL && <div style={{ color:'var(--light)', fontSize:13, padding:'12px 0' }}>FCL quotes use fixed rate structures. Optional add-ons are not applicable.</div>}
          </div>
          <div className="card">
            <div className="card-title">Additional services</div>
            <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
              <input type="checkbox" checked={fields.includeBrokerage} onChange={e=>set('includeBrokerage',e.target.checked)} style={{ width:16,height:16 }} />
              <div>
                <div style={{ fontWeight:500, fontSize:14 }}>Include brokerage fees</div>
                <div style={{ fontSize:12, color:'var(--light)' }}>Customs entry, value-based fee, documentation</div>
              </div>
            </label>
            {fields.includeBrokerage && result?.brokerageLines?.length > 0 && (
              <div style={{ background:'var(--cream)', borderRadius:8, padding:'10px 14px', margin:'8px 0', fontSize:13 }}>
                {result.brokerageLines.map((l,i)=>(
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0' }}>
                    <span>{l.description}</span><span style={{ fontWeight:500 }}>TT${Number(l.ttd).toFixed(2)}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between', marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)', fontWeight:600 }}>
                  <span>Brokerage subtotal</span><span>TT${Number(result.brokerageTotal).toFixed(2)}</span>
                </div>
              </div>
            )}
            <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', cursor:'pointer' }}>
              <input type="checkbox" checked={fields.includeTransport} onChange={e=>set('includeTransport',e.target.checked)} style={{ width:16,height:16 }} />
              <div>
                <div style={{ fontWeight:500, fontSize:14 }}>Include local delivery</div>
                <div style={{ fontSize:12, color:'var(--light)' }}>Door delivery across Trinidad</div>
              </div>
            </label>
            <div className="field">
              <label className="label">Internal notes (not on PDF)</label>
              <textarea className="textarea" style={{ minHeight:72 }} value={fields.notes} onChange={e=>set('notes',e.target.value)} placeholder="Special instructions…" />
            </div>
          </div>
        </div>
      )}

      {/* TAB 3 — Transport */}
      {tab===3 && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title">Delivery details</div>
            <div className="field">
              <label className="label">Distance from El Socorro (km)</label>
              <input className="input" type="number" value={fields.distanceKm} onChange={e=>set('distanceKm',e.target.value)} placeholder="Enter driving km from El Socorro" />
            </div>
            <div className="field">
              <label className="label">Zone (auto-detected)</label>
              <select className="select" value={fields.transportZoneId} onChange={e=>set('transportZoneId',parseInt(e.target.value))}>
                {transportZones.map(z=>(
                  <option key={z.zone} value={z.zone}>Zone {z.zone} — {z.example_areas} ({z.distance_min}–{z.distance_max} km)</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="label">Waiting time (minutes beyond free {transportParams.free_waiting_minutes||30} min)</label>
              <input className="input" type="number" value={fields.waitingMinutes} onChange={e=>set('waitingMinutes',e.target.value)} placeholder="Leave blank if none" />
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {[
                ['requiresSecondMan', 'Second handler required', `+TT$${transportParams.second_man_ttd||75}`],
                ['tailLift', 'Tail-lift required', `+TT$${transportParams.tail_lift_ttd||80}`],
                ['afterHours', 'After-hours delivery (outside 8am–5pm)', `+TT$${transportParams.after_hours_ttd||100}`],
              ].map(([key,label,hint])=>(
                <label key={key} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
                  <input type="checkbox" checked={fields[key]} onChange={e=>set(key,e.target.checked)} style={{ width:16,height:16 }} />
                  <div><div style={{ fontWeight:500, fontSize:14 }}>{label}</div><div style={{ fontSize:12, color:'var(--light)' }}>{hint}</div></div>
                </label>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-title">Delivery estimate</div>
            {transportZones.find(z=>z.zone===fields.transportZoneId)?.base_rate_ttd > 0 ? (
              transportResult ? (
                <>
                  {transportResult.lines.map((l,i)=>(
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                      <span>{l.description}</span><span style={{ fontWeight:500 }}>TT${Number(l.ttd).toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={{ display:'flex', justifyContent:'space-between', marginTop:12, fontWeight:700, fontSize:15 }}>
                    <span>Delivery total</span><span>TT${Number(transportResult.total).toFixed(2)}</span>
                  </div>
                </>
              ) : <div style={{ color:'var(--light)', fontSize:13 }}>Enter details to calculate</div>
            ) : (
              <div style={{ background:'#FFF3E0', borderRadius:8, padding:'12px 14px', fontSize:13, color:'#E65100' }}>
                Zone rates not yet configured. Go to <strong>Rates & pricing → Transport rates</strong> to set zone base rates.
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 4 — Preview */}
      {tab===4 && result && (
        <div className="quote-doc">
          <div className="quote-doc-header">
            <div className="quote-doc-company">
              <div className="quote-doc-company-name">{settings?.company_name||'PAS Trinidad Limited'}</div>
              <div className="quote-doc-company-sub">{settings?.company_address}</div>
              <div className="quote-doc-company-sub">{settings?.company_email}</div>
            </div>
            <div className="quote-doc-num-box">
              <div className="quote-doc-num">QUOTATION PREVIEW</div>
              <div className="quote-doc-date">{new Date().toLocaleDateString('en-TT',{day:'2-digit',month:'short',year:'numeric'})}</div>
            </div>
          </div>
          <div className="quote-doc-body">
            <div className="quote-meta-grid">
              <div className="quote-meta-item"><label>Client</label><span>{fields.clientName||'—'}</span></div>
              <div className="quote-meta-item"><label>Service</label><span>{SERVICES.find(s=>s.value===fields.service)?.label}</span></div>
              <div className="quote-meta-item"><label>Origin</label><span>{fields.origin}</span></div>
              <div className="quote-meta-item"><label>Validity</label><span>{fields.validity}</span></div>
              {!isFCL && <>
                <div className="quote-meta-item"><label>CFT</label><span>{fmt(result.cft)}</span></div>
                <div className="quote-meta-item"><label>Chargeable wt.</label><span>{fmt(result.chargeableWeight)} lbs</span></div>
              </>}
              <div className="quote-meta-item"><label>ROE</label><span>TT${fields.roe}</span></div>
              <div className="quote-meta-item"><label>Commercial value</label><span>USD${fields.commercialValue}</span></div>
            </div>

            <table className="quote-line-table">
              <thead><tr><th>Description</th><th>USD</th><th>TTD</th></tr></thead>
              <tbody>
                {result.lines.map((l,i)=>(
                  l.header ? (
                    <tr key={i}><td colSpan={3} style={{ background:'var(--cream)', color:'var(--mid)', fontWeight:600, fontSize:12, textTransform:'uppercase', letterSpacing:'0.06em', padding:'8px 14px' }}>{l.description.replace('── ','').replace(' ──','')}</td></tr>
                  ) : (
                    <tr key={i}>
                      <td>{l.description}</td>
                      <td>{l.usd!=null?'$'+fmt(l.usd):'—'}</td>
                      <td>{l.ttd!=null?'TT$'+fmt(l.ttd):'—'}</td>
                    </tr>
                  )
                ))}
                {fields.includeBrokerage && result.brokerageLines?.length > 0 && <>
                  <tr><td colSpan={3} style={{ background:'var(--cream)', color:'var(--mid)', fontWeight:600, fontSize:12, textTransform:'uppercase', letterSpacing:'0.06em', padding:'8px 14px' }}>Brokerage</td></tr>
                  {result.brokerageLines.map((l,i)=>(
                    <tr key={'b'+i}><td>{l.description}</td><td>—</td><td>TT${fmt(l.ttd)}</td></tr>
                  ))}
                </>}
                {fields.includeTransport && transportResult?.lines?.length > 0 && <>
                  <tr><td colSpan={3} style={{ background:'var(--cream)', color:'var(--mid)', fontWeight:600, fontSize:12, textTransform:'uppercase', letterSpacing:'0.06em', padding:'8px 14px' }}>Local Delivery</td></tr>
                  {transportResult.lines.map((l,i)=>(
                    <tr key={'t'+i}><td>{l.description}</td><td>—</td><td>TT${fmt(l.ttd)}</td></tr>
                  ))}
                </>}
              </tbody>
              <tfoot>
                <tr className="quote-total-row">
                  <td>Total estimated charges</td>
                  <td>{result.totalUSD>0?'$'+fmt(result.totalUSD):'—'}</td>
                  <td>TT${fmt(grandTotal)}</td>
                </tr>
              </tfoot>
            </table>
            {result.cifValueTTD > 0 && fields.includeBrokerage && (
              <div style={{ fontSize:11, color:'var(--light)', marginTop:8 }}>CIF value (for brokerage): TT${fmt(result.cifValueTTD)}</div>
            )}
            <div className="quote-disclaimer">* Rates are estimates only. Final invoice reflects actual cargo weight, dimensions and charges at time of shipment.</div>
          </div>
          <div style={{ padding:'0 28px 24px', display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button className="btn btn-outline" onClick={()=>saveQuote('draft')} disabled={saving}>Save draft</button>
            <button className="btn btn-outline" onClick={()=>savedId && window.open(api.url(`/quotes/${savedId}/pdf`)+'?token='+(api.getToken()||''),'_blank')} disabled={!savedId}>Download PDF</button>
            <button className="btn btn-green" onClick={openSend} disabled={saving}>Send to client ↗</button>
          </div>
        </div>
      )}

      {tab===4 && !canPreview && (
        <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:14, padding:32, maxWidth:480, margin:'0 auto' }}>
          <div style={{ fontSize:24, marginBottom:12 }}>📋</div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:18, marginBottom:6 }}>Almost ready</div>
          <div style={{ fontSize:13, color:'var(--light)', marginBottom:20 }}>Complete the following before previewing your quote:</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {[
              { done: !!(fields.clientName?.trim() || fields.clientId), label:'Client name or selection', tab:0 },
              { done: !!(fields.commercialValue), label:'Commercial value (USD)', tab:0 },
              { done: !!(fields.roe), label:'Rate of exchange', tab:0 },
              { done: !!(fields.validity), label:'Validity period', tab:0 },
              { done: !!(fields.length && fields.width && fields.height), label:'Cargo dimensions (L × W × H)', tab:1 },
              { done: !!(fields.qty), label:'Quantity', tab:1 },
              { done: !!(fields.actualWeightLbs), label:'Actual weight (lbs)', tab:1 },
            ].map((item, i) => (
              <div key={i}
                onClick={() => !item.done && setTab(item.tab)}
                style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderRadius:8, background: item.done ? '#EAF7F2' : '#FFF3E0', cursor: item.done ? 'default' : 'pointer', border: `1px solid ${item.done ? '#B7E4D0' : '#FFD580'}` }}>
                <div style={{ width:22, height:22, borderRadius:'50%', background: item.done ? '#1D9E75' : '#F59E0B', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ color:'#fff', fontSize:12, fontWeight:700 }}>{item.done ? '✓' : '!'}</span>
                </div>
                <div style={{ fontSize:13, fontWeight: item.done ? 400 : 500, color: item.done ? '#0F6E56' : '#92400E' }}>
                  {item.label}
                  {!item.done && <span style={{ fontSize:11, color:'#B45309', marginLeft:6 }}>← click to fill in</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom nav */}
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:20 }}>
        <button className="btn btn-outline" onClick={()=>setTab(t=>Math.max(0,t-1))} disabled={tab===0}>← Previous</button>
        <button className="btn btn-primary" onClick={()=>setTab(t=>Math.min(TABS.length-1,t+1))} disabled={tab===TABS.length-1}>Next →</button>
      </div>

      {/* Send modal */}
      {sendModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setSendModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-title">Send quotation by email</div>
            <div className="field"><label className="label">Recipient email</label><input className="input" type="email" value={sendTo} onChange={e=>setSendTo(e.target.value)} /></div>
            <div className="field"><label className="label">Subject</label><input className="input" value={sendSubject} onChange={e=>setSendSubject(e.target.value)} /></div>
            <div className="field"><label className="label">Message</label><textarea className="textarea" value={sendBody} onChange={e=>setSendBody(e.target.value)} rows={6}/></div>
            <div style={{ background:'var(--cream)', borderRadius:8, padding:'10px 14px', fontSize:12, color:'var(--light)' }}>PDF quote will be automatically attached.</div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={()=>setSendModal(false)}>Cancel</button>
              <button className="btn btn-green" onClick={doSend} disabled={saving||!sendTo}>{saving?'Sending…':'Send email with PDF'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
